import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { assign, isArray } from 'lodash';
import * as Yup from 'yup';
import { format, setHours, setMinutes, setSeconds } from 'date-fns';

import { Report } from '~/components';
import { RadioGroup, Autocomplete, DateTime, Select } from '~/components/Form';
import { Select as Selectv2 } from '~/components/Form/v2';
import { t } from '~/helpers';
import { getEveryMonth, getYearsInterval } from '~/helpers/dateTime';
import api from '~/services/api';

import * as S from './styles';

export default function DlgRelResumoGeral() {
  const { profile } = useSelector((state) => state.user);

  const reportRef = useRef(null);

  const [open, setOpen] = useState(true);
  const [report, setReport] = useState('RESUMOGERAL');
  const [tank, setTank] = useState({});
  const [shift, setShift] = useState({ initial: [], final: [] });
  const [shiftForm, setShiftForm] = useState({ initial: {}, final: {} });
  const [tipoRel, setTipoRel] = useState('ambiente');

  const optionsTypeReport = useMemo(
    () => [
      { label: 'Manifestado', value: 'manifestado' },
      { label: 'Ambiente', value: 'ambiente' },
      { label: '20º', value: '20' },
    ],
    []
  );

  const optionsGeneral = useMemo(
    () => [
      { label: 'Evolution Mensal', value: 'RESUMOGERALEVOLUTIVO' },
      { label: 'Evolution Turno', value: 'RESUMOGERALEVOLUTIVOTURNO' },
      { label: 'Período', value: 'RESUMOGERAL' },
      { label: 'Mensal', value: 'RESUMOGERALMENSAL' },
    ],
    []
  );

  const optionsTypeRadio = useMemo(
    () => [
      { label: 'PDF', value: 'pdf' },
      { label: 'Excel', value: 'xls' },
    ],
    []
  );

  const months = useMemo(
    () =>
      getEveryMonth().map((month, key) => ({
        value: key + 1,
        label: month,
      })),
    [getEveryMonth]
  );

  const years = useMemo(
    () =>
      getYearsInterval(10).map((year) => ({
        value: year,
        label: year,
      })),
    [getYearsInterval]
  );

  const handleValidations = useCallback(
    async (data) => {
      if (report === 'RESUMOGERALEVOLUTIVO') {
        const schema = Yup.object().shape({
          codposto: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          mesi: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          anoi: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          mesf: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          anof: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });
      }

      if (report === 'RESUMOGERALEVOLUTIVOTURNO') {
        const schema = Yup.object().shape({
          codposto: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          mesano_i: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          mesano_f: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });
      }

      if (report === 'RESUMOGERAL') {
        const schema = Yup.object().shape({
          datainicial: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          datafinal: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });
      }

      if (report === 'RESUMOGERALMENSAL') {
        const schema = Yup.object().shape({
          mes: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
          ano: Yup.string()
            .typeError('Campo obrigatório')
            .required('Campo obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });
      }
    },
    [report]
  );

  const handleOnChangeReport = useCallback((value) => {
    setReport(value);
  }, []);

  const handleModifyReportData = useCallback(
    (values) => {
      delete values.general;

      const formData = assign(values, {
        idtarefapath: "dlgRelResumoGeral.swf",
        usuario: profile.username,
        strusuario: profile.name,
      });

      if (
        ['RESUMOGERALEVOLUTIVO', 'RESUMOGERALEVOLUTIVOTURNO'].includes(report)
      ) {
        formData.strposto = tank.post_desc;
      }

      if (report === 'RESUMOGERAL') {
        formData.datainicial = `${formData.datainicial} 00:00:00`;
        formData.datafinal = `${formData.datafinal} 23:59:59`;
      }

      if (report === 'RESUMOGERALEVOLUTIVOTURNO') {
        formData.mesi = values.mesano_i.split('/')[1];
        formData.anoi = values.mesano_i.split('/')[2].split(' ')[0];

        formData.mesf = values.mesano_f.split('/')[1];
        formData.anof = values.mesano_f.split('/')[2].split(' ')[0];

        formData.post_cod_posto = values.codposto;
        formData.posto_str = values.strposto;

        formData.turn_datahora_ini =
          shiftForm.initial.turn_datahora_ini || formData.mesano_i;
        formData.turn_datahora_fim =
          shiftForm.final.turn_datahora_fim || formData.mesano_f;
        formData.turn_num_turn_ini = shiftForm.initial.turn_num_turn || '';
        formData.turn_num_turn_fim = shiftForm.final.turn_num_turn || '';

        delete formData.mesano_i;
        delete formData.mesano_f;
      }

      return values;
    },
    [tank, profile, shiftForm, report]
  );

  const handleGetShift = useCallback(
    async (value, position) => {
      try {
        const URL = position === 'initial' ? 'getturnosinicio' : 'getturnosfim';
        const query =
          position === 'initial'
            ? { datahorainicio: value }
            : { datahorafinal: value };

        const { data } = await api.get(`/turno/${URL}`, {
          params: { ...query, codigoposto: tank.post_cod_posto },
        });

        setShift((state) => ({
          ...state,
          [position]: [
            {
              label: 'Nenhum',
              turn_datahora_ini: '',
              turn_datahora_fim: '',
            },
            ...data,
          ].map((item) => ({
            ...item,
            label:
              item.label ||
              `${item.turn_num_turn} - ${
                position === 'initial' ? item.hora_ini : item.hora_fim
              }`,
            value: item.turn_num_turn,
          })),
        }));
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [tank]
  );

  const handleGetChangeTank = useCallback((data) => {
    setTank(data);
    setShift({ initial: [], final: [] });
    setShiftForm({ initial: {}, final: {} });

    reportRef.current.handleClear('turno_inicio');
    reportRef.current.handleClear('turno_fim');
  }, []);

  return (
    <Report
      ref={reportRef}
      open={open}
      title="Relatório de Resumo Geral"
      relatorio={report}
      onClose={() => setOpen(false)}
      serialize={handleModifyReportData}
      initials={{
        tiporel: 'ambiente',
        general: 'RESUMOGERAL',
        mesano_i: format(
          setMinutes(setSeconds(setHours(new Date(), 0), 0), 0),
          'dd/MM/yyyy HH:mm:ss'
        ),
        mesano_f: format(
          setMinutes(setSeconds(setHours(new Date(), 23), 59), 59),
          'dd/MM/yyyy HH:mm:ss'
        ),
        mesi: format(new Date(), 'M'),
        mesf: format(new Date(), 'M'),
        anoi: format(new Date(), 'yyyy'),
        anof: format(new Date(), 'yyyy'),
        mes: format(new Date(), 'M'),
        ano: format(new Date(), 'yyyy'),
        tipo: 'pdf',
      }}
      validate={handleValidations}
    >
      <S.Wrapper>
        <RadioGroup
          row
          name="tiporel"
          label="Tipo"
          options={optionsTypeReport}
          getValue={(value) => setTipoRel(value)}
        />

        <RadioGroup
          row
          name="general"
          label="Geral"
          options={optionsGeneral}
          getValue={handleOnChangeReport}
        />

        {report === 'RESUMOGERALEVOLUTIVO' && (
          <>
            <Autocomplete
              name="codposto"
              label="post_desc"
              url="usuarioposto"
              unique="post_cod_posto"
              title="Posto"
              getValue={(value) => setTank(value)}
              params={{
                wherefield: ['post_controla_20'],
                valuewherefield: [tipoRel === '20' ? 'S' : 'N'],
                typewherefield: ['EQUALS'],
              }}
            />

            <S.Columns grid={2}>
              <Select name="mesi" label="Mês Inicio" options={months} />
              <Select name="anoi" label="Ano Inicio" options={years} />
            </S.Columns>

            <S.Columns grid={2}>
              <Select name="mesf" label="Mês Fim" options={months} />
              <Select name="anof" label="Ano Fim" options={years} />
            </S.Columns>
          </>
        )}

        {report === 'RESUMOGERALEVOLUTIVOTURNO' && (
          <>
            <Autocomplete
              name="codposto"
              label="post_desc"
              url="usuarioposto"
              unique="post_cod_posto"
              title="Posto"
              getValue={(value) => {
                handleGetChangeTank(value);
              }}
              params={{
                wherefield: ['post_controla_20'],
                valuewherefield: [tipoRel === '20' ? 'S' : 'N'],
                typewherefield: ['EQUALS'],
              }}
            />

            <S.Columns grid={2}>
              <DateTime
                time
                name="mesano_i"
                label="Data/Hora Inicial"
                getValue={(value) => {
                  handleGetShift(value, 'initial');
                  setShiftForm((state) => ({ ...state, initial: {} }));
                  reportRef.current.handleClear('turno_inicio');
                }}
              />

              <Selectv2
                name="turno_inicio"
                label="Turno"
                data={shift.initial}
                getValue={(value) =>
                  !!value &&
                  setShiftForm((state) => ({
                    ...state,
                    initial: value,
                  }))
                }
              />
            </S.Columns>

            <S.Columns grid={2}>
              <DateTime
                time
                name="mesano_f"
                label="Data/Hora Final"
                getValue={(value) => {
                  handleGetShift(value, 'final');
                  setShiftForm((state) => ({ ...state, final: {} }));
                  reportRef.current.handleClear('turno_fim');
                }}
              />

              <Selectv2
                name="turno_fim"
                label="Turno"
                data={shift.final}
                getValue={(value) =>
                  !!value &&
                  setShiftForm((state) => ({
                    ...state,
                    final: value,
                  }))
                }
              />
            </S.Columns>
          </>
        )}

        {report === 'RESUMOGERAL' && (
          <>
            <DateTime name="datainicial" label="Data Inicial" />

            <DateTime name="datafinal" label="Data Final" />
          </>
        )}

        {report === 'RESUMOGERALMENSAL' && (
          <>
            <Select name="mes" label="Mês" options={months} />

            <Select name="ano" label="Ano" options={years} />
          </>
        )}

        <RadioGroup
          row
          name="tipo"
          label="Formato"
          options={optionsTypeRadio}
        />
      </S.Wrapper>
    </Report>
  );
}
